import React from "react";
import Navbar from "../components/Navbar/Navbar";
import Footer from "../components/Footer/Footer";

// Hero Banner
import bannerImage from "../components/Assets/engineering_services.webp";

// Sample Icons/Images for Services 
import mechanicalDesign from "../components/Assets/mechanical_design.webp";
import processOptimization from "../components/Assets/process_optimization.webp";
import plcProgramming from "../components/Assets/plc_programming.webp";
import systemIntegration from "../components/Assets/system_integration.webp";

import Card from './../components/Commanbackgroundcard/Card';

const EngineeringServicesPage = () => {
  // Core Services Data
  const services = [
    {
      name: "Mechanical Design",
      image: mechanicalDesign,
      description:
        "Innovative, precise, and reliable designs tailored for long-term manufacturing performance and efficiency.",
    },
    {
      name: "Process Optimization",
      image: processOptimization,
      description:
        "Streamlined workflows that minimize downtime, improve throughput, and reduce operating costs.",
    },
    {
      name: "PLC Programming",
      image: plcProgramming,
      description:
        "Robust and scalable PLC logic delivering seamless automation across production lines.",
    },
    {
      name: "System Integration",
      image: systemIntegration,
      description:
        "Complete interoperability between machines, controls, and software for efficient production.",
    },
  ];

  const industries = [
    { name: "Automotive", icon: "🚗" },
    { name: "Pharmaceuticals", icon: "💊" },
    { name: "FMCG", icon: "📦" },
    { name: "Heavy Engineering", icon: "⚙️" },
    { name: "Electronics", icon: "💡" },
  ];

  const whyChoose = [
    "Expert team with decades of combined engineering experience",
    "Proven success in minimizing downtime & boosting productivity",
    "Custom-tailored solutions for different industries",
    "Focus on quality, safety, and cost-effectiveness",
    "Seamless integration from concept to commissioning",
    "Strong Portfolio of Successful Global Implementations",
  ];

  return (
    <div className="bg-gray-100 min-h-screen">
      <Navbar />

      {/* Page Heading Card */}
      <Card text="ENGINEERING SERVICES" />

      {/* Intro Section */}
      <section className="py-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
            Engineering Excellence for{" "}
            <span className="text-[#F49100]">Manufacturing Innovation</span>
          </h2>
          <p className="text-gray-700 text-base lg:text-lg leading-relaxed max-w-4xl mx-auto">
            Our engineering team delivers comprehensive solutions including
            mechanical design, process optimization, and PLC programming. From
            concept to commissioning, we ensure seamless integration into your
            workflows — reducing downtime, improving efficiency, and maintaining
            world-class quality standards.
          </p>
        </div>
      </section>

      {/* Core Services Section */}
      <section className="py-16 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 text-center mb-4">
            Our Core <span className="text-[#F49100]">Engineering Services</span>
          </h2>
          <p className="text-gray-700 text-center mb-12 max-w-3xl mx-auto">
            End-to-end engineering services designed to improve efficiency,
            quality, and reliability across your production environment.
          </p>
          <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, i) => (
              <article
                key={i}
                className="bg-white rounded-2xl shadow-md hover:shadow-xl transition transform hover:-translate-y-2 duration-500 overflow-hidden border border-gray-100"
              >
                <div className="relative group">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent pointer-events-none"></div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">
                    {service.name}
                  </h3>
                  <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Applications */}
      <section className="py-16 px-6 lg:px-12 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
            Industry <span className="text-[#F49100]">Applications</span>
          </h2>
          <p className="text-gray-700 mb-12 max-w-3xl mx-auto">
            Our engineering services cater to multiple sectors with tailored
            approaches for each industry’s unique challenges.
          </p>
          <div className="grid sm:grid-cols-2 md:grid-cols-5 gap-8 max-w-5xl mx-auto">
            {industries.map((ind, i) => (
              <div
                key={i}
                className="flex flex-col items-center p-6 bg-white rounded-2xl shadow-md hover:shadow-lg transition duration-300 transform hover:-translate-y-1"
              >
                <div className="text-5xl mb-4">{ind.icon}</div>
                <p className="text-gray-700 font-semibold text-center">
                  {ind.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 text-center mb-4">
            Why Choose <span className="text-[#F49100]">Us</span>
          </h2>
          <p className="text-gray-700 text-center mb-12 max-w-3xl mx-auto">
            Partner with a team dedicated to efficiency, precision, and
            long-lasting impact.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {whyChoose.map((point, i) => (
              <div
                key={i}
                className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition duration-300"
              >
                <div className="w-3 h-3 bg-[#F49100] rounded-full mr-4 flex-shrink-0"></div>
                <p className="text-gray-700 font-medium">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-6 lg:px-12 bg-[#F49100] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Ready to Elevate Your Engineering?
          </h2>
          <p className="mb-8 text-lg leading-relaxed">
            Contact us today to implement innovative engineering services
            customized for your manufacturing needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-block px-8 py-4 bg-white text-[#F49100] font-bold rounded-lg hover:bg-gray-100 transition duration-300"
            >
              Get in Touch
            </a>
            {/* <a
              href="/quote"
              className="inline-block px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-[#F49100] transition duration-300"
            >
              Request Quote
            </a> */}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EngineeringServicesPage;
